import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import API from "../../api";

const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function TimedPlayTest() {

const { id } = useParams();
const navigate = useNavigate();
const location = useLocation();

const config = location.state || {};

const mode = config.mode || "overall";
const totalTime = config.totalTime || 20 * 60;
const perQ = config.perQuestion || config.adaptiveTime || 0;

const [test, setTest] = useState(null);
const [answers, setAnswers] = useState({});
const [current, setCurrent] = useState(0);
const [showResult, setShowResult] = useState(false);

const [timeLeft, setTimeLeft] = useState(null);
const [questionTime, setQuestionTime] = useState(null);

const globalTimer = useRef(null);
const questionTimer = useRef(null);

// ---------------- ROUTE GUARD ----------------
useEffect(() => {
 if (!location.state) {
   navigate(`/timed-settings/${id}`, { replace: true });
 }
}, []);

// ---------------- LOAD TEST ----------------
useEffect(() => {

 if (!location.state) return;

 const load = async () => {

 const res = await API.get(`/test-types/timed/${id}`);

const mapped = res.data.questions.map(q => {

 let correctIndex = null;

 if (typeof q.answer === "object" && q.answer !== null) {
   correctIndex = q.answer.index;

 } else if (typeof q.answer === "number") {
   correctIndex = q.answer;

 } else if (typeof q.answer === "string") {

  const val = q.answer.trim().toUpperCase();

  // LETTER BASED (A,B,C,D,E)
  if (/^[A-Z]$/.test(val)) {
    correctIndex = val.charCodeAt(0) - 65;
  }

  // fallback: match by text
  else {
    const found = q.options?.findIndex(
      o => o.text?.toLowerCase() === val.toLowerCase()
    );

    if (found !== -1) correctIndex = found;
  }
}

 return {
   ...q,
   type: q.type?.toLowerCase() || "mcq",
   correctIndex   // 🔥 SINGLE SOURCE OF TRUTH
 };

});

 setTest({ ...res.data, questions: mapped });

 if (mode === "overall" || mode === "hybrid") {
   setTimeLeft(totalTime);
 }

 if (mode === "per") {
   setQuestionTime(perQ);
 }

 };

 load();

 return () => {
   clearInterval(globalTimer.current);
   clearInterval(questionTimer.current);
 };

}, []);

// ---------------- GLOBAL TIMER ----------------
useEffect(() => {

 if (mode !== "overall" && mode !== "hybrid") return;
 if (timeLeft === null) return;

 clearInterval(globalTimer.current);

 globalTimer.current = setInterval(() => {
   setTimeLeft(t => {
     if (t <= 1) {
       clearInterval(globalTimer.current);
       submit();
       return 0;
     }
     return t - 1;
   });
 }, 1000);

 return () => clearInterval(globalTimer.current);

}, [timeLeft]);

// ---------------- PER QUESTION TIMER ----------------
useEffect(() => {

 if (mode !== "per") return;
 if (!test) return;

 clearInterval(questionTimer.current);

 questionTimer.current = setInterval(() => {
   setQuestionTime(t => {
     if (t <= 1) {
       nextAuto();
       return perQ;
     }
     return t - 1;
   });
 }, 1000);

 return () => clearInterval(questionTimer.current);

}, [current, test]);

// ---------------- LOGIC ----------------
const choose = (index) => {
 setAnswers(prev => ({
   ...prev,
   [test.questions[current]._id]: index
 }));

 // Timed mode me immediate result show nahi hoga
 if (mode === "practice") {
   setShowResult(true);
 }
};

const nextAuto = () => {
 setShowResult(false);
 if (current < test.questions.length - 1) {
   setCurrent(p => p + 1);
   setQuestionTime(perQ);
 } else submit();
};

const submit = () => {

 clearInterval(globalTimer.current);
 clearInterval(questionTimer.current);

 let score = 0;

 test.questions.forEach(q => {

  const userAns = answers[q._id];

  if (q.type === "text") {
    if (
      userAns?.toLowerCase().trim() ===
      q.answer?.toLowerCase().trim()
    ) {
      score++;
    }
  } else {
    if (userAns === q.correctIndex) {
      score++;
    }
  }

});

 navigate(`/result/${id}`, {
   state: {
     test,
     answers,
     score,
     total: test.questions.length
   }
 });
};

// ---------------- UI ----------------
if (!test) return <p className="text-center mt-10">Loading Test...</p>;

const q = test.questions[current];
const qId = q._id;

// CORRECT INDEX
const correctIndex = q.correctIndex;

return (

<div className="min-h-screen bg-slate-100 flex">

<aside className="w-64 bg-white shadow-xl p-4 hidden md:block">

<h2 className="text-center font-bold mb-4">Questions</h2>

<div className="grid grid-cols-4 gap-2">

{test.questions.map((_, i) => (
<button
key={i}
onClick={() => {
 setCurrent(i);
 setShowResult(false);
}}
className={`h-9 rounded-full text-sm font-semibold
${i === current ? "bg-blue-600 text-white" : "bg-gray-200"}`}
>
{i + 1}
</button>
))}

</div>
</aside>

<main className="flex-1 p-6">

<div className="flex justify-between items-center mb-5">

<h1 className="text-xl font-bold">{test.title}</h1>

<div className="flex gap-3">

{(mode === "overall" || mode === "hybrid") && (
<div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg font-bold">
⏱ {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}
</div>
)}

{mode === "per" && (
<div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-bold">
Q ⏳ {questionTime}s
</div>
)}

</div>
</div>

<div className="bg-white rounded-2xl shadow-lg p-6">

<h2 className="font-semibold mb-4">
Q{current + 1}. {q.question}
</h2>

<div className="mb-3">
  <span
    className={`px-3 py-1 text-sm rounded-full font-semibold
      ${
        q.type === "mcq"
          ? "bg-blue-100 text-blue-700"
          : q.type === "truefalse"
          ? "bg-purple-100 text-purple-700"
          : "bg-gray-200 text-gray-700"
      }
    `}
  >
    {q.type === "mcq"
      ? "Multiple Choice"
      : q.type === "truefalse"
      ? "True / False"
      : "Text Question"}
  </span>
</div>


{q.media?.url && (
 q.media.type === "video" ?
 <video
   src={`${BASE_URL}${q.media.url}`}
   controls
   className="h-48 mb-4 rounded"
 />
 :
 <img
   src={`${BASE_URL}${q.media.url}`}
   className="h-48 mb-4 rounded"
 />
)}

{/* MCQ */}
{q.type === "mcq" && q.options?.map((opt, i) => {

  const chosen = answers[qId] === i;
  const isCorrect = i === correctIndex;
  const mediaUrl = opt.media?.url ? `${BASE_URL}${opt.media.url}` : null;

  return (
    <div
      key={i}
      onClick={() => choose(i)}
      className={`border rounded-xl p-4 mb-4 cursor-pointer
        ${mode === "practice" && showResult && isCorrect ? "bg-green-100 border-green-500" : ""}
        ${mode === "practice" && showResult && chosen && !isCorrect ? "bg-red-100 border-red-500" : ""}
        ${chosen && !showResult ? "bg-blue-100 border-blue-500" : "hover:bg-slate-100"}
      `}
    >
      <p className="mb-2">{opt.text}</p>

      {mediaUrl && (
        <img src={mediaUrl} className="max-h-48 rounded mx-auto" />
      )}
    </div>
  );
})}
</div>

{q.type === "truefalse" && (
  <div className="flex gap-4">
    {["True", "False"].map((val, i) => (
      <button
        key={val}
        onClick={() => choose(i)}
        className={`px-6 py-3 rounded-lg border font-semibold
          ${answers[qId] === i
            ? "bg-blue-100 border-blue-500"
            : "bg-white border-gray-300"}
        `}
      >
        {val}
      </button>
    ))}
  </div>
)}

{q.type === "text" && (
  <div>
    <input
      type="text"
      value={answers[qId] || ""}
      onChange={(e) =>
        setAnswers(prev => ({
          ...prev,
          [qId]: e.target.value
        }))
      }
      placeholder="Type your answer..."
      className="border p-3 rounded w-full"
    />

    {mode === "practice" && (
      <button
        onClick={() => setShowResult(true)}
        className="mt-3 bg-blue-600 text-white px-4 py-2 rounded"
      >
        Check Answer
      </button>
    )}
  </div>
)}

<div className="flex justify-between mt-6">

<button
disabled={current === 0}
onClick={() => {
 setCurrent(p => p - 1);
 setShowResult(false);
}}
className="bg-gray-600 text-white px-6 py-2 rounded">
Prev
</button>

{current === test.questions.length - 1 ?

<button onClick={submit}
className="bg-green-600 text-white px-6 py-2 rounded">
Submit
</button>

:

<button
onClick={() => {
 setCurrent(p => p + 1);
 setShowResult(false);
}}
className="bg-blue-600 text-white px-6 py-2 rounded">
Next
</button>

}

</div>

</main>
</div>

);
}